// frontend/src/pages/PhotoInboxPage.tsx
// The selected tank's photo inbox (/livestock/photos, plan 4.3): photos
// uploaded with Add Photos wait here, oldest first, until they are sorted in
// the assign view (/livestock/photos/:id).

import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  AspectRatio,
  Button,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useIntersection } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCircleCheck,
  IconFish,
  IconPhotoPlus,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useTank } from '@/contexts/TankContext';
import { EmptyState } from '@/components/common/EmptyState';
import { NoTankSelected } from '@/components/common/NoTankSelected';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useLivestockList } from '@/hooks/useLivestock';
import {
  useInboxCount,
  useInboxList,
  useInboxSessionCount,
  useRefetchOnImageError,
} from '@/hooks/usePhotoInbox';
import { AddLivestockModal } from '@/features/livestock/AddLivestockModal';
import { AddPhotosButton } from '@/features/livestock/AddPhotosButton';
import { InboxPhotoGrid } from '@/features/livestock/InboxPhotoGrid';
import { useSortRecords } from '@/features/livestock/photoInboxSorting';
import { RecentlySorted } from '@/features/livestock/RecentlySorted';

function GridSkeleton(): JSX.Element {
  return (
    <SimpleGrid cols={{ base: 3, xs: 4, sm: 5, md: 6, lg: 8 }} spacing={{ base: 8, sm: 'sm' }}>
      {Array.from({ length: 12 }, (_, i) => (
        <AspectRatio key={i} ratio={1}>
          <Skeleton radius="md" />
        </AspectRatio>
      ))}
    </SimpleGrid>
  );
}

export function PhotoInboxPage(): JSX.Element {
  const { selectedTank } = useTank();
  const isMobile = useIsMobile();
  const count = useInboxCount();
  const list = useInboxList();
  const assignedThisSession = useInboxSessionCount();
  const { data: livestock } = useLivestockList();
  const [addLivestockOpen, setAddLivestockOpen] = useState(false);
  const onImageError = useRefetchOnImageError(list.refetch);

  // Load the next page before the end of the grid scrolls into view.
  const { ref: endRef, entry } = useIntersection({ rootMargin: '600px' });
  const nearEnd = entry?.isIntersecting ?? false;
  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = list;
  // Not again after a page failed (the end stays in view): Try again asks for it.
  useEffect(() => {
    if (nearEnd && hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
      void fetchNextPage();
    }
  }, [nearEnd, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  // Photos being assigned or discarded in the background are as good as gone.
  const sortRecords = useSortRecords(selectedTank?.id);
  const sending = useMemo(
    () =>
      new Set(
        sortRecords.filter((record) => record.status === 'pending').map((record) => record.photoId)
      ),
    [sortRecords]
  );
  const pages = list.data?.pages;
  const photos = useMemo(
    () => pages?.flatMap((page) => page.items).filter((photo) => !sending.has(photo.id)) ?? [],
    [pages, sending]
  );

  if (!selectedTank) {
    return (
      <Container size="lg" py="xl">
        <NoTankSelected purpose="to sort its photos" />
      </Container>
    );
  }

  const empty = list.isSuccess && photos.length === 0 && !hasNextPage;
  const noLivestock = !!livestock && livestock.livestock.length === 0;
  const toSort = count.data !== undefined ? Math.max(0, count.data - sending.size) : photos.length;
  // Something was sorted this session: the empty inbox is "All sorted", not a first visit.
  const sortedThisSession =
    assignedThisSession > 0 || sortRecords.some((record) => record.status === 'done');
  const addLivestock = (): void => setAddLivestockOpen(true);

  return (
    <Container size="xl" py={{ base: 'md', sm: 'xl' }} px={{ base: 0, sm: 'md' }}>
      <Stack gap="lg">
        <Group justify="space-between" wrap="nowrap" align="center">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            <ActionIcon
              component={Link}
              to="/livestock"
              variant="subtle"
              color="gray"
              size={isMobile ? 44 : 'lg'}
              aria-label="Back to livestock"
            >
              <IconArrowLeft size={16} />
            </ActionIcon>
            <div style={{ minWidth: 0 }}>
              <Title order={1} fz={{ base: 24, sm: 30 }} lh={1.2}>
                Photos to sort
              </Title>
              {!empty && (
                <Text size="sm" c="gray.7" truncate style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <span>{`${toSort} to sort`}</span> · {selectedTank.name}
                </Text>
              )}
            </div>
          </Group>
          {!empty && (
            <AddPhotosButton fromInbox>
              {(props): JSX.Element =>
                isMobile ? (
                  <ActionIcon {...props} variant="light" size={44} aria-label="Add Photos">
                    <IconPhotoPlus size={16} />
                  </ActionIcon>
                ) : (
                  <Button {...props} variant="light" leftSection={<IconPhotoPlus size={16} />}>
                    Add Photos
                  </Button>
                )
              }
            </AddPhotosButton>
          )}
        </Group>

        {noLivestock && photos.length > 0 && (
          <Paper withBorder radius={12} px="md" py="sm">
            <Group justify="space-between" gap="xs">
              <Group gap="xs" wrap="nowrap">
                <IconFish size={16} color="var(--mantine-color-teal-6)" style={{ flexShrink: 0 }} />
                <Text size="sm">
                  {selectedTank.name} has no livestock yet. Add it here, or while sorting.
                </Text>
              </Group>
              <Button size="compact-sm" variant="light" onClick={addLivestock}>
                Add livestock
              </Button>
            </Group>
          </Paper>
        )}

        {list.isError && !isFetchNextPageError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Photos didn't load">
            <Group gap="sm">
              <Text size="sm">{list.error.message}</Text>
              <Button
                size="compact-sm"
                variant="light"
                color="red"
                onClick={() => void list.refetch()}
              >
                Try again
              </Button>
            </Group>
          </Alert>
        )}

        {list.isLoading && <GridSkeleton />}

        <RecentlySorted
          tankId={selectedTank.id}
          organisms={livestock?.livestock}
          defaultOpen={empty}
        />

        {empty && sortedThisSession && (
          <EmptyState
            icon={IconCircleCheck}
            title={
              assignedThisSession > 0
                ? `All sorted · ${assignedThisSession} assigned this session`
                : 'All sorted'
            }
            description="Every photo in the inbox has gone to its organism or been discarded."
            primaryAction={{ label: 'Back to livestock', to: '/livestock' }}
          >
            <AddPhotosButton fromInbox>
              {(props): JSX.Element => (
                <Button {...props} variant="subtle" leftSection={<IconPhotoPlus size={16} />}>
                  Add Photos
                </Button>
              )}
            </AddPhotosButton>
          </EmptyState>
        )}

        {empty && !sortedThisSession && (
          <EmptyState
            icon={IconPhotoPlus}
            title="Sort photos later"
            description={`Pick photos from your library with Add Photos: they upload to ${selectedTank.name}'s inbox without any questions. Sort them here when you have a minute: open a photo, pick the organism, Assign.`}
          >
            <AddPhotosButton fromInbox>
              {(props): JSX.Element => (
                <Button {...props} size="md" leftSection={<IconPhotoPlus size={16} />}>
                  Add Photos
                </Button>
              )}
            </AddPhotosButton>
            {noLivestock && (
              <Button variant="subtle" size="sm" onClick={addLivestock}>
                Add livestock first
              </Button>
            )}
          </EmptyState>
        )}

        {photos.length > 0 && <InboxPhotoGrid photos={photos} onImageError={onImageError} />}

        {hasNextPage && (
          <Group justify="center" gap="xs" ref={endRef}>
            {isFetchNextPageError && <Text size="sm">More photos didn&apos;t load.</Text>}
            <Button
              variant="subtle"
              loading={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {isFetchNextPageError ? 'Try again' : 'Load more'}
            </Button>
          </Group>
        )}
      </Stack>

      <AddLivestockModal
        opened={addLivestockOpen}
        onClose={() => setAddLivestockOpen(false)}
        onSuccess={() => setAddLivestockOpen(false)}
        tankId={selectedTank.id}
      />
    </Container>
  );
}
